/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';

import {ReportsService} from '../../+reports/reports.service';
import {FieldType} from '../../shared/form-generator/fields/fields.interfaces';
import {GroupZoneType} from '../../shared/form-generator/form-generator.interfaces';
import {GPTranslateService} from '../../shared/localization/translate.service';
import {toGroupTypeList} from '../elasticquery.service';
import {OutputField} from '../reports.interfaces';

@Component({
  selector: 'gp-column-item',
  templateUrl: './column-item.component.html',
  styleUrls: ['./column-item.component.scss']
})

export class ColumnItemComponent implements OnChanges {

  @Input() public config: OutputField;
  @Input() public enabledFields: string[];
  @Output() public removeColumnItem: EventEmitter<string> = new EventEmitter<string>();
  @Output() public recalculateOrders: EventEmitter<string> = new EventEmitter<string>();

  constructor(private gpTranslateService: GPTranslateService,
              private reportsService: ReportsService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.config.metadatas.disabled = !this.isFieldEnabled(this.config);
    this.initDisplayEntityPathInConfig(this.config.field);
  }

  public transIdsDisplay(transIds: string[]): string {
    return this.gpTranslateService.getDisplayTranslation(transIds, ' | ');
  }

  public initDisplayEntityPathInConfig(field: any) {
    const entityPaths = this.reportsService.entityPathsToDisplay;
    const index = entityPaths.indexOf(field);

    if (this.config.displayEntityPath && index === -1) {
      entityPaths.push(field);
      this.reportsService.markAsChanged();
    } else if (!this.config.displayEntityPath && index !== -1) {
      entityPaths.splice(index, 1);
      this.reportsService.markAsChanged();
    }
  }

  public changeSortDirection() {
    if (!this.config.fieldSortedBy) {
      this.config.fieldSortedBy = 'asc';
      this.config.fieldSortPosition = 10000;
    } else if (this.config.fieldSortedBy.toLowerCase() === 'asc') {
      this.config.fieldSortedBy = 'desc';
      this.config.fieldSortPosition = 10000;
    } else {
      this.config.fieldSortedBy = '';
      this.config.fieldSortPosition = 1;
    }
    this.recalculateOrders.next(this.config.field);
  }

  public increaseSortOrder() {
    // 1.5 will be recalculated
    this.config.fieldSortPosition = this.config.fieldSortPosition + 1.5;
    this.recalculateOrders.next(this.config.field);
  }

  public isOrderVisible() {
    return this.config.fieldSortedBy;
  }

  public removeColumn() {
    this.removeColumnItem.next(this.config.field);
    this.recalculateOrders.next(this.config.field);
  }

  public isFieldEnabled(item: OutputField) {
    return this.enabledFields && this.enabledFields.length && this.enabledFields.includes(item.field);
  }

  public hasPayValue(): boolean {
    return this.config.fieldTypes.includes(FieldType.PAY_VALUE);
  }

  public isSortingDisabled(): boolean {
    return this.config.metadatas.disabled ||
      toGroupTypeList(this.config.fieldGroupType ?? []).some(groupType =>
        groupType === GroupZoneType.MULTIPLE_MANDATORY.toString() ||
        groupType === GroupZoneType.MULTIPLE_NON_MANDATORY.toString()) ||
      this.config.fieldTypes.includes(FieldType.FILE) || this.config.fieldTypes.includes(FieldType.FILES);
  }

  public isFileType(): boolean {
    return this.config.fieldTypes.includes(FieldType.FILE) || this.config.fieldTypes.includes(FieldType.FILES);
  }

  public isEntityType(): boolean {
    return this.config.fieldTypes.includes(FieldType.ENTITY);
  }
}
